import { ScreeningCategory } from '../types';

/**
 * Screening questions configuration.
 * Easy to modify - just update this array to change questions.
 *
 * Based on Legal Ops qualification criteria (Dec 2024):
 * - 5 Parent Categories: Liability, Coverage, Damages, Treatment, Other (Representation)
 * - Child criteria can override "Unknown" on parent categories
 */
export const SCREENING_QUESTIONS: ScreeningCategory[] = [
  // 1. Liability
  {
    id: 'liability',
    letter: 'L',
    mainQuestion: {
      id: 'liability_assigned',
      text: 'Was fault assigned to someone else?',
    },
    subQuestionsOnYes: [
      {
        id: 'police_report',
        text: 'Does a police report exist?',
        // If yes, show text fields for report number and police department
        hasTextFields: true,
        textFieldLabels: ['Report Number', 'Police Department'],
      },
      { id: 'defendant_ticket', text: 'Did the defendant receive a ticket?' },
      { id: 'defendant_admitted', text: 'Did the defendant admit fault?' },
    ],
  },
  // 2. Damages
  {
    id: 'damages',
    letter: 'D',
    mainQuestion: {
      id: 'injured',
      text: 'Are you injured?',
      hasTextField: true,
      textFieldPlaceholder:
        'Describe your injuries... (e.g., back pain, neck pain, headaches, etc.)',
      textFieldLabel: 'Injury Details',
    },
    // Damages sub-questions: If ANY are Yes → can override Unknown on other parent categories
    subQuestionsOnYes: [
      {
        id: 'broken_bones',
        text: 'Did you break any bones?',
        isOverrideTrigger: true,
      },
      {
        id: 'hospital_stay',
        text: 'Were you an inpatient at a hospital?',
        isOverrideTrigger: true,
      },
      { id: 'surgery', text: 'Did you have surgery?', isOverrideTrigger: true },
      {
        id: 'head_trauma',
        text: 'Did you experience head trauma?',
        isOverrideTrigger: true,
      },
    ],
  },
  // 3. Treatment
  {
    id: 'treatment',
    letter: 'M',
    mainQuestion: {
      id: 'sought_treatment',
      text: 'Did you seek or are you willing to receive medical care?',
      hasTextField: true,
      textFieldLabel: 'Medical Providers',
      textFieldPlaceholder: 'ER, chiropractor, doctor...',
      showTextFieldOnAnswer: 'yes',
    },
    // No sub-questions per new spec
  },
  // 4. Coverage
  {
    id: 'coverage',
    letter: 'C',
    mainQuestion: {
      id: 'defendant_insured',
      text: 'Is the defendant insured?',
      hasTextField: true,
      alwaysShowTextFields: true,
      textFieldLabels: [
        '3P Carrier Name',
        '3P Policy Number',
        '3P Claim Number',
        '1P Carrier Name',
        '1P Policy Number',
        '1P Claim Number',
      ],
    },
    // Coverage A: Sub-questions when defendant IS insured
    // If ANY are Yes → can override Unknown on other parent categories
    subQuestionsOnYes: [
      {
        id: 'commercial_policy',
        text: 'Does the defendant have a commercial policy?',
        // Note: Carrier Name and Policy Number fields are now on the main question
        // (always visible as "3P Carrier Name" and "3P Policy Number")
        isOverrideTrigger: true, // Can override Unk on other parents
      },
      {
        id: 'uber_lyft',
        text: 'Were you in an Uber or Lyft?',
        isOverrideTrigger: true,
      },
      {
        id: 'govt_vehicle',
        text: 'Was the defendant driving a government vehicle?',
        isOverrideTrigger: true,
      },
    ],
    // Coverage B: Backup questions when defendant is NOT insured
    // If UM/UIM is Yes → coverage is satisfied
    backupQuestionsOnNo: [
      {
        id: 'client_auto_insurance',
        text: 'Do you have auto insurance?',
        // Note: Carrier Name and Policy Number fields are now on the main question
        // (always visible as "1P Carrier Name" and "1P Policy Number")
      },
      {
        id: 'um_uim',
        text: 'Do you have UM/UIM benefits?',
      },
    ],
  },
  // 5. Prior Rep
  {
    id: 'representation',
    letter: 'R',
    mainQuestion: {
      id: 'no_current_lawyer',
      text: 'Are you without legal representation at this time?',
      hasTextField: true,
      textFieldLabel: 'Previous Firm Name',
      textFieldPlaceholder: 'Name of previous firm',
      showTextFieldOnAnswer: 'no', // Only show when they have prior representation
    },
  },
];
